import { Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import type { AdminWardDto } from "~/lib/api";

export interface ArchiveWardDialogProps {
  /** The ward to archive, or null when the dialog is closed. */
  ward: AdminWardDto | null;
  archiving: boolean;
  error?: string | null;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation before archiving a ward (HAN-86). Archiving is a soft-delete: the
 * ward is hidden from ward-selection but kept for history/audit, so the copy
 * reassures rather than warns of data loss.
 */
export function ArchiveWardDialog({
  ward,
  archiving,
  error = null,
  onConfirm,
  onOpenChange,
}: ArchiveWardDialogProps): JSX.Element {
  return (
    <Dialog
      open={ward !== null}
      onOpenChange={(open) => {
        if (!open && !archiving) onOpenChange(false);
      }}
    >
      <DialogContent>
        {ward && (
          <>
            <DialogHeader>
              <DialogTitle>Archive {ward.name}?</DialogTitle>
              <DialogDescription>
                This ward will be hidden from ward selection, but its handovers and history are
                kept. You can&rsquo;t start new handovers on an archived ward.
              </DialogDescription>
            </DialogHeader>

            {error ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" disabled={archiving} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button variant="destructive" disabled={archiving} onClick={onConfirm}>
                {archiving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                Archive ward
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
